// Pure helpers behind the main map's company focus: which operator names a company
// owns, company search, in-view grouping, and the company label for popups. Kept free
// of React so they run under node:test via vite-node (scripts/test-operator-focus.ts).

export type Bbox = [number, number, number, number];

// One row of /api/pipelines/operators: an operator name with the companies that own it
// (api-server/lib/pipeline-companies.js). `owners` is empty for an unlisted name, which
// stands as its own company.
export interface OperatorCatalogEntry {
  operator: string;
  owners: string[];
  operatedBy: string | null;
  segments: number;
  gas_transmission_segments: number;
  bbox: Bbox | null;
}

export interface InViewOperator {
  name: string;
  count: number;
}

export interface CompanyHit {
  company: string;
  names: number;
  segments: number;
  via: string | null; // the operator name that matched when the company name did not
}

export interface CompanyGroup {
  company: string;
  count: number;
  members: (InViewOperator & { jvWith: string[] })[];
}

export interface FocusSummary {
  label: string;
  names: number;
  jv: number;
}

// The companies an operator name belongs to: its owners, or the name itself when unlisted.
export function companiesOf(entry: OperatorCatalogEntry): string[] {
  return entry.owners.length ? entry.owners : [entry.operator];
}

// Every operator name a company owns or co-owns, sorted: the list the server focus takes.
export function operatorsForCompany(catalog: OperatorCatalogEntry[], company: string): string[] {
  return catalog.filter(e => companiesOf(e).includes(company)).map(e => e.operator).sort();
}

// Company-name matches first, then companies reached through one of their operator
// names. Each company appears once; larger companies rank first within each tier.
export function searchCompanies(catalog: OperatorCatalogEntry[], query: string, limit = 20): CompanyHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const stats = new Map<string, CompanyHit>();
  for (const e of catalog) {
    for (const company of companiesOf(e)) {
      const hit = stats.get(company) ?? { company, names: 0, segments: 0, via: null };
      hit.names += 1;
      hit.segments += e.segments;
      if (!hit.via && e.operator !== company && e.operator.toLowerCase().includes(q)) hit.via = e.operator;
      stats.set(company, hit);
    }
  }
  const bySize = (a: CompanyHit, b: CompanyHit) => b.segments - a.segments || a.company.localeCompare(b.company);
  const hits = [...stats.values()];
  const byName = hits.filter(h => h.company.toLowerCase().includes(q)).map(h => ({ ...h, via: null }));
  const byVia = hits.filter(h => !h.company.toLowerCase().includes(q) && h.via);
  return [...byName.sort(bySize), ...byVia.sort(bySize)].slice(0, limit);
}

export function focusSummary(
  company: string | undefined, focus: string[], lookup: Map<string, OperatorCatalogEntry>,
): FocusSummary | null {
  if (focus.length === 0) return null;
  const jv = focus.filter(name => (lookup.get(name)?.owners.length ?? 0) > 1).length;
  const label = company ?? (focus.length === 1 ? focus[0] : `${focus.length} names`);
  return { label, names: focus.length, jv };
}

// In-view names grouped by company. A JV name sits under its operator's group, or its
// first owner's when the operator is unknown; an unlisted name is its own group.
export function groupByCompany(inView: InViewOperator[], lookup: Map<string, OperatorCatalogEntry>): CompanyGroup[] {
  const groups = new Map<string, CompanyGroup>();
  for (const op of inView) {
    const entry = lookup.get(op.name);
    const company = entry?.operatedBy ?? entry?.owners[0] ?? op.name;
    const jvWith = entry && entry.owners.length > 1 ? entry.owners.filter(o => o !== company) : [];
    const group = groups.get(company) ?? { company, count: 0, members: [] };
    group.count += op.count;
    group.members.push({ ...op, jvWith });
    groups.set(company, group);
  }
  const byCount = (a: { count: number }, b: { count: number }) => b.count - a.count;
  const out = [...groups.values()].sort(byCount);
  for (const group of out) group.members.sort(byCount);
  return out;
}

// Popup and detail-panel label: "Kinder Morgan", or "JV · operated by A · with B".
// Null when the name is unlisted, or when a single owner already leads the operator name.
export function companyLabel(entry: OperatorCatalogEntry | undefined): string | null {
  if (!entry || entry.owners.length === 0) return null;
  if (entry.owners.length === 1) {
    const [company] = entry.owners;
    return entry.operator.toLowerCase().startsWith(company.toLowerCase()) ? null : company;
  }
  if (!entry.operatedBy) return `JV · ${entry.owners.join(', ')}`;
  const others = entry.owners.filter(o => o !== entry.operatedBy);
  return `JV · operated by ${entry.operatedBy} · with ${others.join(', ')}`;
}

export function unionBbox(bboxes: (Bbox | null)[]): Bbox | null {
  let out: Bbox | null = null;
  for (const b of bboxes) {
    if (!b) continue;
    out = out
      ? [Math.min(out[0], b[0]), Math.min(out[1], b[1]), Math.max(out[2], b[2]), Math.max(out[3], b[3])]
      : [b[0], b[1], b[2], b[3]];
  }
  return out;
}
